import React from "react";
import { Box, Typography } from "@mui/material";
import { Layers, MessageCircle, Plus, ReceiptText, ShieldCheck } from "lucide-react";

import AppButton from "../../../../ui/AppButton";
import AppSearch from "../../../../ui/AppSearch";
import palette from "../../../../../theme/palette";

// Cabecera superior del formulario: titulo, contador, buscador y accion principal.
export default function TrHeader({
  titulo,
  contador,
  contadorTexto,
  nuevoTexto,
  buscarTexto,
  valorBusqueda,
  nuevoDeshabilitado,
  ticketModo,
  ticketOpciones,
  onTicketModoChange,
  onNuevo,
  onBuscar,
  compactControles = false,
  headerExtra = null,
  nuevoActionId,
  sx = {},
}) {
  const ticketOptions = ticketOpciones || [
    { value: "completo", label: "Completo", icon: Layers },
    { value: "admin", label: "Paquete", icon: ShieldCheck },
    { value: "cliente", label: "Cliente", icon: ReceiptText },
    { value: "whatsapp", label: "WhatsApp", icon: MessageCircle },
  ];

  const controlButtonSx = compactControles
    ? {
        height: 38,
        minHeight: 38,
        px: 1.25,
        gap: 0.6,
        fontSize: "12px",
      }
    : {};
  const searchWidth = compactControles ? { xs: "100%", sm: 220 } : undefined;
  const searchHeight = compactControles ? 38 : undefined;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        justifyContent: "space-between",
        alignItems: { xs: "flex-start", sm: "center" },
        gap: compactControles ? { xs: 0.5, sm: 0.75 } : { xs: 1, sm: 2 },
        mb: compactControles ? { xs: 0.5, md: 0.5 } : { xs: 1.25, md: 3 },
        ...sx,
      }}
    >
      <Box>
        <Typography sx={{ color: palette.text, fontWeight: 700, fontSize: "22px", lineHeight: 1.2 }}>
          {titulo}
        </Typography>
        <Typography sx={{ color: palette.muted, fontSize: "13px", mt: 0.5 }}>
          {contador} {contadorTexto}
        </Typography>
        {onTicketModoChange && (
          <Box sx={{ display: "flex", alignItems: "center", gap: compactControles ? 0.35 : 0.55, mt: compactControles ? 0.45 : 0.85, flexWrap: "wrap" }}>
            <Typography sx={{ color: palette.muted, fontSize: "10.5px", fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.35 }}>
              Ticket pred.
            </Typography>
            {ticketOptions.map(({ value, label, icon: Icon }) => {
              const active = ticketModo === value;

              return (
                <Box
                  key={value}
                  component="button"
                  type="button"
                  onClick={() => onTicketModoChange(value)}
                  title={value === "whatsapp" ? "Enviar ticket por WhatsApp al grabar" : `Imprimir por defecto: Ticket ${label}`}
                  sx={{
                    height: 25,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.35,
                    px: 0.75,
                    borderRadius: "6px",
                    border: `1px solid ${active ? palette.accent : palette.borderSoft}`,
                    backgroundColor: active ? palette.accentSoft : "rgba(255,255,255,0.025)",
                    color: active ? palette.accent : palette.muted,
                    fontSize: "11px",
                    fontWeight: 800,
                    cursor: "pointer",
                    lineHeight: 1,
                    boxShadow: active ? "inset 0 0 0 1px rgba(255,255,255,0.03)" : "none",
                    transition: "all .16s ease",
                    "&:hover": {
                      borderColor: palette.accent,
                      color: palette.accent,
                      backgroundColor: palette.accentSoft,
                    },
                  }}
                >
                  <Icon size={12} />
                  {label}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          alignSelf: { xs: "stretch", sm: compactControles ? "flex-start" : "center" },
          justifyContent: "flex-end",
          gap: compactControles ? { xs: 0.5, sm: 0.5 } : 1,
          width: { xs: "100%", sm: "auto" },
          flexWrap: compactControles ? { xs: "wrap", sm: "wrap", lg: "nowrap" } : { xs: "wrap", md: "nowrap" },
        }}
      >
        <AppSearch
          placeholder={buscarTexto}
          value={valorBusqueda}
          onChange={onBuscar}
          width={searchWidth}
          height={searchHeight}
        />

        {headerExtra}

        <AppButton
          icon={<Plus size={18} />}
          data-action-id={nuevoActionId}
          onClick={onNuevo}
          disabled={nuevoDeshabilitado}
          sx={{
            ...controlButtonSx,
            backgroundColor: nuevoDeshabilitado ? palette.chip : palette.accent,
            borderColor: nuevoDeshabilitado ? palette.border : palette.accent,
            color: nuevoDeshabilitado ? palette.muted : palette.onAccent,
            fontWeight: 800,
            ml: compactControles ? 0 : { xs: "auto", sm: 0 },
            "&:hover": {
              backgroundColor: nuevoDeshabilitado ? palette.chip : palette.accent,
              borderColor: nuevoDeshabilitado ? palette.border : palette.accent,
              color: nuevoDeshabilitado ? palette.muted : palette.onAccent,
            },
          }}
        >
          {nuevoTexto}
        </AppButton>
      </Box>
    </Box>
  );
}
